import React, { useEffect, useState, useMemo } from "react";
import { Button, Modal, Alert } from "react-bootstrap";
import {
  IconPlus,
  // IconShoppingCart,
  IconArrowRight,
  IconCategory,
} from "@tabler/icons-react";
import LayoutDashboard from "layouts/layout-dashboard-collapse";
import { useInventory } from "./context/InventoryContext";
import { CategoryProvider, useCategory } from "./context/CategoryContext";
import { WarehouseProvider, useWarehouse } from "./context/WarehouseContext";
import InventoryForm from "./components/InventoryForm";
import ImageUploadModal from "./components/ImageUploadModal";
import StockUpdateModal from "./components/StockUpdateModal";
import InventoryHistory from "./components/InventoryHistory";
import ItemHistoryModal from "./components/ItemHistoryModal";
import InventoryStats from "./components/InventoryStats";
import InventoryTable from "./components/InventoryTable";
import CategoryManagement from "./components/CategoryManagement";
import TransferModal from "./components/TransferModal";
import BulkTransferModal from "./components/BulkTransferModal";
import WarehouseManagement from "./components/WarehouseManagement";
import StockUpdateModalV2 from "./components/StockUpdateModalV2";
import ManualStockUpdateModal from "./components/ManualStockUpdateModal";
import Swal from "sweetalert2";
import { useDeleteData } from "hooks/useApiHooks";
import PrintJson from "utils/printJson";
import PermissionMiddleware from "middleware/PermissionMiddleware";
import { API_URL } from "utils/constants";
import WorkshopTopbar from "./components/WorkshopTopbar";

export default function InventoryPage() {
  return (
    <CategoryProvider>
      <WarehouseProvider>
        <InventoryPageContent />
      </WarehouseProvider>
    </CategoryProvider>
  );
}

function InventoryPageContent() {
  const {
    inventory,
    statistics,
    refetch,
    Pagination,
    handleSearch,
    handleCategoryFilter,
    handleStatusFilter,
    handleWarehouseFilter,
    handleLowStockFilter,
    handleStatusWarehouseFilter,
    handleActiveFilter,
    handleSortFilter,
    filterOptions,
  } = useInventory();

  const { categories } = useCategory();
  const { warehouses } = useWarehouse();

  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  // const [activeTab, setActiveTab] = useState("inventory");
  const [searchTerm, setSearchTerm] = useState("");
  const [showStockUpdateModal, setShowStockUpdateModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showItemHistoryModal, setShowItemHistoryModal] = useState(false);
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const [showBulkUpdateModalV2, setShowBulkUpdateModalV2] = useState(false);
  const [selectedItemForHistory, setSelectedItemForHistory] = useState(null);
  const [
    statusFilter,
    //  setStatusFilter
  ] = useState("all");
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedItemForTransfer, setSelectedItemForTransfer] = useState(null);
  const [showBulkTransferModal, setShowBulkTransferModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showWarehouseModal, setShowWarehouseModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageItem, setSelectedImageItem] = useState(null);
  const [showManualStockModal, setShowManualStockModal] = useState(false);
  const [selectedItemForManualUpdate, setSelectedItemForManualUpdate] = useState(null);

  const { deleteData } = useDeleteData("/inventory", "inventory");

  // Calculate statistics from API or fallback to client-side calculation
  const stats = useMemo(() => {
    // If we have statistics from API, use them
    if (statistics.data) {
      // Extract warehouse-specific data
      const warehouses = statistics.data.warehouses || [];
      const officeWarehouse = warehouses.find(
        (w) => w.warehouse_code === "OFF",
      );
      const mechanicWarehouse = warehouses.find(
        (w) => w.warehouse_code === "BMK",
      );

      return {
        totalItems: statistics.data.total_items || 0,
        lowStockCount: statistics.data.low_stock_count || 0,
        outOfStockCount: statistics.data.out_of_stock_count || 0,
        totalValue: statistics.data.total_value || 0,
        uniqueLabels: statistics.data.unique_labels || 0,
        officeLowStock: officeWarehouse?.low_stock_items || 0,
        officeOutOfStock: officeWarehouse?.out_of_stock_items || 0,
        mechanicLowStock: mechanicWarehouse?.low_stock_items || 0,
        mechanicOutOfStock: mechanicWarehouse?.out_of_stock_items || 0,
      };
    }

    // Fallback to client-side calculation
    const items = inventory.data || [];

    // For low stock filter, count unique items (not per-warehouse records)
    const uniqueItems = new Map();
    items.forEach((item) => {
      if (!uniqueItems.has(item.id)) {
        uniqueItems.set(item.id, item);
      }
    });

    const uniqueItemArray = Array.from(uniqueItems.values());

    const lowStockItems = uniqueItemArray.filter((item) => {
      // Check if any warehouse has low stock
      return (
        item.warehouse_stocks?.some((ws) => ws.stock <= ws.minimum_stock) ||
        false
      );
    });

    // Count low stock by warehouse
    const officeLowStock = items.filter((item) => {
      const officeStock = item.warehouse_stocks?.find(
        (ws) => ws.warehouse_code === "OFF",
      );
      return officeStock && officeStock.stock <= officeStock.minimum_stock;
    }).length;

    const mechanicLowStock = items.filter((item) => {
      const mechanicStock = item.warehouse_stocks?.find(
        (ws) => ws.warehouse_code === "BMK",
      );
      return (
        mechanicStock && mechanicStock.stock <= mechanicStock.minimum_stock
      );
    }).length;

    const totalValue = uniqueItemArray.reduce(
      (sum, item) => sum + (item.current_stock_total || 0) * (item.price || 0),
      0,
    );
    const uniqueLabels = new Set(
      uniqueItemArray.flatMap((item) => item.labels || []),
    ).size;

    return {
      totalItems: uniqueItemArray.length,
      lowStockCount: lowStockItems.length,
      officeLowStock,
      mechanicLowStock,
      totalValue,
      uniqueLabels,
    };
  }, [inventory.data, statistics.data]);

  // Filter items based on search, category, and status (client-side for unsupported API filters)
  const filteredItems = useMemo(() => {
    let items = inventory.data || [];

    // For low stock endpoint, the data is already filtered by the API
    // No additional client-side filtering needed for status
    if (statusFilter === "good") {
      items = items.filter((item) => {
        // Item is good if it has stock above minimum in at least one warehouse
        return (
          item.warehouse_stocks?.some((ws) => ws.stock > ws.minimum_stock) ||
          false
        );
      });
    } else if (statusFilter === "empty") {
      items = items.filter((item) => {
        // Item is empty if total stock is 0
        return (item.current_stock_total || 0) === 0;
      });
    }
    // "all" and "low" are handled by the API, so no client-side filtering needed

    return items;
  }, [inventory.data, statusFilter]);

  // Get category data for display
  const getCategoryData = (categoryId) => {
    if (!Array.isArray(categories))
      return { name: "Uncategorized", color: "#6b7280" };
    const category = categories.find((cat) => cat.id === categoryId);
    return category
      ? {
          name: category.name,
          color: category.color || "#6b7280",
        }
      : { name: "Uncategorized", color: "#6b7280" };
  };

  // Group items by category
  // const groupedItems = useMemo(() => {
  //   const groups = {};
  //   filteredItems.forEach((item) => {
  //     const category = item.category_id || "uncategorized";
  //     if (!groups[category]) {
  //       groups[category] = [];
  //     }
  //     groups[category].push(item);
  //   });
  //   return groups;
  // }, [filteredItems]);

  const handleCreateItem = () => {
    setSelectedItem(null);
    setShowModal(true);
  };

  const handleEditItem = (item) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  const handleDeleteItem = async (item) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Do you want to delete "${item.name}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await deleteData(item.id);
        Swal.fire("Deleted!", "Item has been deleted.", "success");
        refetch();
      } catch (error) {
        Swal.fire("Error!", "Failed to delete item.", "error");
      }
    }
  };

  const handleToggleActive = async (item) => {
    try {
      const newStatus = item.is_active === false ? true : false;
      const payload = {
        name: item.name,
        nama_alias: item.nama_alias,
        category_id: item.category_id,
        unit: item.unit,
        description: item.description,
        current_stock: item.current_stock_total,
        minimum_stock: item.minimum_stock,
        price: item.price,
        is_active: newStatus,
        warehouse_stocks: item.warehouse_stocks || [],
      };

      const response = await fetch(`${API_URL}/inventory/${item.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Failed to update status");
      
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: `Item sekarang ${newStatus ? 'Aktif' : 'Tidak Aktif'}`,
        showConfirmButton: false,
        timer: 2000
      });
      refetch();
    } catch (error) {
      Swal.fire("Error", "Gagal memperbarui status", "error");
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedItem(null);
  };

  const handleFormSuccess = () => {
    setShowModal(false);
    setSelectedItem(null);
    refetch();
    Swal.fire("Success!", "Item has been saved.", "success");
  };

  const handleFormError = (error) => {
    Swal.fire("Error!", error.message || "Failed to save item.", "error");
  };

  const handleShowItemHistory = (item) => {
    setSelectedItemForHistory(item);
    setShowItemHistoryModal(true);
  };

  const handleTransferStock = (item) => {
    setSelectedItemForTransfer(item);
    setShowTransferModal(true);
  };

  const handleBulkTransfer = () => {
    setShowBulkTransferModal(true);
  };

  // const handleBulkUpdate = () => {
  //   setShowBulkUpdateModal(true);
  // };

  // const handleBulkUpdateV2 = () => {
  //   setShowBulkUpdateModalV2(true);
  // };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm !== (filterOptions.search || "")) {
        handleSearch(searchTerm);
      }
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, filterOptions.search, handleSearch]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleImageClick = (item) => {
    setSelectedImageItem(item);
    setShowImageModal(true);
  };

  return (
    <LayoutDashboard>
      <div className="container-fluid mt-3 workshop-page-scope">
        <WorkshopTopbar />
        <div
          className="workshop-content-panel"
          style={{
            minHeight: "100vh",
            backgroundColor: "#f9fafb",
            padding: "16px",
          }}
        >
          {/* Header */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
            <div className="mb-3 mb-md-0">
              <h2 className="h4 h-md-h2 mb-1 text-dark">
                Workshop Inventory Management
              </h2>
              <p className="text-muted small mb-0">
                Kelola stok alat dan sparepart workshop
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <PermissionMiddleware allowedRoles={["superadmin"]}>
                <Button
                  variant="secondary"
                  onClick={() => setShowWarehouseModal(true)}
                  className="d-flex align-items-center gap-1"
                >
                  <span className="d-none d-sm-inline">
                    Warehouse Management
                  </span>
                </Button>
              </PermissionMiddleware>
              <Button
                variant="info"
                onClick={() => setShowCategoryModal(true)}
                className="d-flex align-items-center gap-1"
              >
                <IconCategory size={16} />
                <span className="d-none d-sm-inline">Manage Categories</span>
              </Button>
              <Button
                variant="success"
                onClick={handleCreateItem}
                className="d-flex align-items-center gap-1"
              >
                <IconPlus size={16} />
                <span className="d-none d-sm-inline">Tambah Item</span>
              </Button>
              <Button
                variant="warning"
                onClick={handleBulkTransfer}
                className="d-flex align-items-center gap-1"
              >
                <IconArrowRight size={16} />
                <span className="d-none d-sm-inline">Bulk Transfer</span>
              </Button>

              {/* <Button
                variant="primary"
                onClick={handleBulkUpdate}
                className="d-flex align-items-center gap-1"
              >
                <IconShoppingCart size={16} />
                <span className="d-none d-sm-inline">Bulk Update Stock</span>
              </Button> */}
              {/* bulk update modal V2 */}
              {/* <Button
                variant="info"
                onClick={handleBulkUpdateV2}
                className="d-flex align-items-center gap-1"
              >
                <IconShoppingCart size={16} />
                <span className="d-none d-sm-inline">Bulk Update Stock</span>
              </Button> */}
            </div>
          </div>

          {/* Statistics Cards */}
          <InventoryStats
            stats={stats}
            onStatusFilter={handleStatusFilter}
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            onSearch={handleSearch}
            filterOptions={filterOptions}
            categories={categories}
            onCategoryFilter={handleCategoryFilter}
            getCategoryData={getCategoryData}
            onWarehouseFilter={handleWarehouseFilter}
            onLowStockFilter={handleLowStockFilter}
            onStatusWarehouseFilter={handleStatusWarehouseFilter}
            activeFilter={filterOptions.is_active}
            onActiveFilter={handleActiveFilter}
          />

          {/* Error Display */}
          {inventory.error && (
            <Alert variant="danger" style={{ marginBottom: "24px" }}>
              <strong>Error loading inventory:</strong> {inventory.error}
            </Alert>
          )}

          {/* Inventory Table */}
          <InventoryTable
            inventory={inventory}
            filteredItems={filteredItems}
            filterOptions={filterOptions}
            handleSortFilter={handleSortFilter}
            getCategoryData={getCategoryData}
            onEditItem={handleEditItem}
            onDeleteItem={handleDeleteItem}
            onToggleActive={handleToggleActive}
            onUpdateStock={(item) => {
              setSelectedItem(item);
              setShowStockUpdateModal(true);
            }}
            onShowItemHistory={handleShowItemHistory}
            onTransferStock={handleTransferStock}
            onImageClick={handleImageClick}
            onUpdateStockManually={(item) => {
              setSelectedItemForManualUpdate(item);
              setShowManualStockModal(true);
            }}
            Pagination={Pagination}
          />
          <div className="mt-5">
            Dev Tools
            <PrintJson data={inventory} />
          </div>
        </div>
      </div>
      {/* CRUD Modal */}
      <InventoryForm
        show={showModal}
        onHide={handleModalClose}
        initialData={selectedItem}
        onSuccess={handleFormSuccess}
        onError={handleFormError}
        categories={categories || []}
        warehouses={warehouses || []}
      />
      {/* Stock Update Modal */}
      <StockUpdateModal
        show={showStockUpdateModal}
        onHide={() => {
          setShowStockUpdateModal(false);
          setSelectedItem(null);
        }}
        selectedItem={selectedItem}
        items={inventory.data || []}
        warehouses={warehouses || []}
        onSuccess={() => {
          setShowStockUpdateModal(false);
          setSelectedItem(null);
          refetch();
        }}
        onError={(error) => {
          console.error("Stock update error:", error);
        }}
      />
      {/* Bulk Update Modal */}
      <StockUpdateModal
        show={showBulkUpdateModal}
        onHide={() => {
          setShowBulkUpdateModal(false);
        }}
        selectedItem={null}
        items={inventory.data || []}
        warehouses={warehouses || []}
        onSuccess={() => {
          setShowBulkUpdateModal(false);
          refetch();
        }}
        onError={(error) => {
          console.error("Bulk update error:", error);
        }}
      />
      {/* Inventory History Modal */}
      <InventoryHistory
        show={showHistoryModal}
        onHide={() => setShowHistoryModal(false)}
      />
      {/* Item History Modal */}
      <ItemHistoryModal
        show={showItemHistoryModal}
        onHide={() => {
          setShowItemHistoryModal(false);
          setSelectedItemForHistory(null);
        }}
        itemId={selectedItemForHistory?.id}
        itemName={selectedItemForHistory?.name}
      />
      {/* Transfer Modal */}
      <TransferModal
        show={showTransferModal}
        onHide={() => {
          setShowTransferModal(false);
          setSelectedItemForTransfer(null);
        }}
        selectedItem={selectedItemForTransfer}
        warehouses={warehouses || []}
        onSuccess={() => {
          setShowTransferModal(false);
          setSelectedItemForTransfer(null);
          refetch();
        }}
        onError={(error) => {
          console.error("Transfer error:", error);
        }}
      />
      {/* Bulk Transfer Modal */}
      <BulkTransferModal
        show={showBulkTransferModal}
        onHide={() => setShowBulkTransferModal(false)}
        warehouses={warehouses || []}
        onSuccess={() => {
          setShowBulkTransferModal(false);
          refetch();
        }}
        onError={(error) => {
          console.error("Bulk transfer error:", error);
        }}
      />
      {/* showBulkUpdateModalV2 */}
      <StockUpdateModalV2
        show={showBulkUpdateModalV2}
        onHide={() => {
          setShowBulkUpdateModalV2(false);
        }}
        selectedItem={null}
        items={inventory.data || []}
        warehouses={warehouses || []}
        onSuccess={() => {
          setShowBulkUpdateModalV2(false);
          refetch();
        }}
        onError={(error) => {
          console.error("Bulk update V2 error:", error);
        }}
      />
      {/* Category Management Modal */}
      <Modal
        show={showCategoryModal}
        onHide={() => setShowCategoryModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Manage Categories</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CategoryManagement />
        </Modal.Body>
      </Modal>
      {/* Warehouse Management Modal */}
      <Modal
        show={showWarehouseModal}
        onHide={() => setShowWarehouseModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Warehouse Management</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <WarehouseManagement />
        </Modal.Body>
      </Modal>
      {/* Image Upload Modal */}
      <ImageUploadModal
        show={showImageModal}
        onHide={() => {
          setShowImageModal(false);
          setSelectedImageItem(null);
        }}
        item={selectedImageItem}
        onSuccess={() => {
          setShowImageModal(false);
          setSelectedImageItem(null);
          refetch();
          Swal.fire("Success!", "Image has been uploaded.", "success");
        }}
        onError={(error) => {
          Swal.fire("Error!", error.message || "Failed to upload image.", "error");
        }}
      />
      {/* Manual Stock Update Modal */}
      <ManualStockUpdateModal
        show={showManualStockModal}
        onHide={() => {
          setShowManualStockModal(false);
          setSelectedItemForManualUpdate(null);
        }}
        item={selectedItemForManualUpdate}
        warehouses={warehouses || []}
        onSuccess={() => {
          refetch();
        }}
      />
    </LayoutDashboard>
  );
}
